import React, { createContext, useContext, useState, useEffect } from 'react';

const STORAGE_KEY = 'hoja_prefs';
const SOURCE_LANGUAGE = 'en';

const DEFAULT_PREFS = {
  targetLanguage: 'fr',
  // True once the user picks a language in Settings. Until then the default
  // applies, even though the whole prefs object (default language included)
  // is saved on every visit.
  targetLanguageChosen: false,
  // Auto-pause is off unless the learner turns it on (the Auto-pause button
  // on the student page, or Settings); as with the language, a stored value
  // only sticks once it was chosen there.
  pauseOnEverySubtitle: false,
  pauseOnEverySubtitleChosen: false,
  pauseIfUnknownWords: false,
  knownWordIds: [],
  skipKnownConfirm: false,
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw);
    // Merge stored values over defaults so newly added fields get sensible defaults.
    const prefs = { ...DEFAULT_PREFS, ...parsed };
    if (!prefs.targetLanguageChosen) prefs.targetLanguage = DEFAULT_PREFS.targetLanguage;
    if (!prefs.pauseOnEverySubtitleChosen) prefs.pauseOnEverySubtitle = DEFAULT_PREFS.pauseOnEverySubtitle;
    return prefs;
  } catch (e) {
    return { ...DEFAULT_PREFS };
  }
}

const UserPreferencesContext = createContext(undefined);

export function UserPreferencesProvider({ children }) {
  const [prefs, setPrefs] = useState(loadPrefs);

  // Persist the whole preference object whenever it changes.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) {
      /* ignore quota / serialization errors */
    }
  }, [prefs]);

  const value = {
    ...prefs,
    // sourceLanguage is immutable — exposed as a constant with no setter.
    sourceLanguage: SOURCE_LANGUAGE,
    setTargetLanguage: (lang) =>
      setPrefs((p) => ({ ...p, targetLanguage: lang, targetLanguageChosen: true })),
    setPauseOnEverySubtitle: (b) =>
      setPrefs((p) => ({ ...p, pauseOnEverySubtitle: b, pauseOnEverySubtitleChosen: true })),
    setPauseIfUnknownWords: (b) =>
      setPrefs((p) => ({ ...p, pauseIfUnknownWords: b })),
    addKnownWord: (id) =>
      setPrefs((p) =>
        p.knownWordIds.includes(id)
          ? p
          : { ...p, knownWordIds: [...p.knownWordIds, id] }
      ),
    removeKnownWord: (id) =>
      setPrefs((p) => ({
        ...p,
        knownWordIds: p.knownWordIds.filter((x) => x !== id),
      })),
    clearKnownWords: () => setPrefs((p) => ({ ...p, knownWordIds: [] })),
    setSkipKnownConfirm: (b) =>
      setPrefs((p) => ({ ...p, skipKnownConfirm: b })),
  };

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function usePreferences() {
  const ctx = useContext(UserPreferencesContext);
  if (ctx === undefined) {
    throw new Error(
      'usePreferences must be used within a UserPreferencesProvider'
    );
  }
  return ctx;
}
